import { html, nothing } from "lit";
import { formatAgo } from "../format.ts";

export type SessionHistoryPanelProps = {
  sessionKey: string;
  messages: unknown[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
};

interface ChatMessage {
  role?: string;
  content?: unknown;
  createdAt?: string | number;
  [key: string]: unknown;
}

function extractMessageText(msg: unknown): string {
  const m = msg as ChatMessage;
  const content = m.content;
  if (typeof content === "string") {
    return content;
  }
  if (Array.isArray(content)) {
    return content
      .map((p) => {
        const item = p as Record<string, unknown>;
        if (item.type === "text" && typeof item.text === "string") {
          return item.text;
        }
        return null;
      })
      .filter((v): v is string => typeof v === "string")
      .join("\n");
  }
  return "";
}

function formatRole(role: string): string {
  switch (role) {
    case "user":
      return "User";
    case "assistant":
      return "Assistant";
    case "system":
      return "System";
    case "tool":
      return "Tool";
    default:
      return role.charAt(0).toUpperCase() + role.slice(1);
  }
}

function renderMessage(msg: unknown, index: number) {
  const m = msg as ChatMessage;
  const role = m.role ?? "unknown";
  const text = extractMessageText(m);
  const createdAt =
    m.createdAt != null
      ? formatAgo(typeof m.createdAt === "number" ? m.createdAt : Date.parse(String(m.createdAt)))
      : null;

  const isUser = role === "user";
  const isAssistant = role === "assistant";
  const isSystem = role === "system";
  const isTool = role === "tool";

  const roleClass = isUser
    ? "msg-user"
    : isAssistant
      ? "msg-assistant"
      : isSystem
        ? "msg-system"
        : isTool
          ? "msg-tool"
          : "";

  return html`
    <div class="history-message ${roleClass}" data-index=${index}>
      <div class="history-message-header">
        <span class="history-message-role">${formatRole(role)}</span>
        ${createdAt ? html`<span class="history-message-time muted">${createdAt}</span>` : nothing}
      </div>
      <div class="history-message-content">
        ${
          text
            ? html`<pre class="history-message-text">${text}</pre>`
            : html`
                <span class="muted" style="font-style: italic">(empty message)</span>
              `
        }
      </div>
    </div>
  `;
}

export function renderSessionHistoryPanel(props: SessionHistoryPanelProps) {
  const { sessionKey, messages, loading, error, onClose } = props;

  return html`
    <div class="session-history-overlay" @click=${(e: Event) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div class="session-history-panel">
        <div class="session-history-header">
          <div class="session-history-title">
            <span>Session History</span>
            <span class="mono muted" style="font-size: 11px; margin-left: 8px;">${sessionKey}</span>
          </div>
          <button class="btn icon" @click=${onClose} title="Close panel" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div class="session-history-body">
          ${
            loading
              ? html`
                  <div class="session-history-state">
                    <div class="spinner"></div>
                    <div class="muted">Loading history…</div>
                  </div>
                `
              : error
                ? html`
                    <div class="callout danger session-history-state">
                      <strong>Error loading history:</strong> ${error}
                    </div>
                  `
                : messages.length === 0
                  ? html`
                      <div class="session-history-state muted">No messages found in this session.</div>
                    `
                  : html`
                      <div class="session-history-messages">
                        ${messages.map((msg, i) => renderMessage(msg, i))}
                      </div>
                    `
          }
        </div>
      </div>
    </div>
  `;
}
